import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  insuranceProviderPlanTypesService,
  CreateInsuranceProviderPlanTypeInput,
  UpdateInsuranceProviderPlanTypeInput,
} from "../../services/admin/insurance-provider-plan-types.service";

export function useInsuranceProviderPlanTypesAdmin() {
  return useQuery({
    queryKey: ["admin", "insurance-provider-plan-types"],
    queryFn: insuranceProviderPlanTypesService.getAll,
  });
}

export function useInsuranceProviderPlanType(id?: string) {
  return useQuery({
    queryKey: ["admin", "insurance-provider-plan-types", id],
    queryFn: () => insuranceProviderPlanTypesService.getById(id!),
    enabled: !!id,
  });
}

export function useInsuranceProviderPlanTypesByProvider(providerId?: string) {
  return useQuery({
    queryKey: [
      "admin",
      "insurance-provider-plan-types",
      "provider",
      providerId,
    ],
    queryFn: () =>
      insuranceProviderPlanTypesService.getByProviderId(providerId!),
    enabled: !!providerId,
  });
}

export function useCreateInsuranceProviderPlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInsuranceProviderPlanTypeInput) =>
      insuranceProviderPlanTypesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-provider-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance provider plan type created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.message || "Failed to create insurance provider plan type",
        color: "red",
      });
    },
  });
}

export function useUpdateInsuranceProviderPlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateInsuranceProviderPlanTypeInput) =>
      insuranceProviderPlanTypesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-provider-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance provider plan type updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.message || "Failed to update insurance provider plan type",
        color: "red",
      });
    },
  });
}

export function useDeleteInsuranceProviderPlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insuranceProviderPlanTypesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-provider-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance provider plan type deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.message || "Failed to delete insurance provider plan type",
        color: "red",
      });
    },
  });
}
